import React, { useEffect, useState, useRef } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { isClerkEnabled } from '../hooks/useClerkSafe'
import { useTheme } from '../hooks/useTheme'
import { setGetToken } from '../api/client'
import { setLiveSessionGetToken } from '../hooks/useLiveSession'
import { fetchSubscriptionStatus } from '../store/slices/subscriptionSlice'
import { setDailyLimit } from '../store/slices/evaluationSlice'
import { trackSignUp } from '../lib/analytics'
import type { AppDispatch, RootState } from '../store'
import { isDev } from '../data/env'
import { SUPPORTED_LANGUAGES } from '../i18n'

// Import Clerk — these won't crash on import, only when rendered outside ClerkProvider
import { SignedIn, SignedOut, UserButton, useAuth } from '@clerk/clerk-react'

function ClerkAuthSync() {
  const { getToken, isSignedIn, userId } = useAuth()
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    setGetToken(getToken)
    setLiveSessionGetToken(getToken)
  }, [getToken])

  useEffect(() => {
    if (isSignedIn) {
      dispatch(fetchSubscriptionStatus()).then((action) => {
        if (fetchSubscriptionStatus.fulfilled.match(action) && action.payload.isPro) {
          dispatch(setDailyLimit(-1))
        }
      })
      // Fire sign_up conversion exactly once per user (first time this browser
      // ever sees them signed in). Guarded by localStorage so repeat visits
      // don't inflate the conversion count.
      if (userId) {
        const key = `tajweed_signup_tracked_${userId}`
        if (!localStorage.getItem(key)) {
          trackSignUp()
          localStorage.setItem(key, '1')
        }
      }
    }
  }, [isSignedIn, userId, dispatch])

  return null
}

function LanguagePicker() {
  const { i18n, t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)
    ?? SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language?.split('-')[0])
    ?? SUPPORTED_LANGUAGES[0]

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t('languagePicker.ariaLabel')}
        className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white transition-colors flex items-center gap-1.5 text-sm"
      >
        <span>{current.flag}</span>
        <span className="hidden sm:inline text-xs">{current.label}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-2 min-w-[170px] max-h-[400px] overflow-y-auto">
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => { i18n.changeLanguage(l.code); setOpen(false) }}
              className={clsx(
                'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors',
                i18n.language === l.code || i18n.language?.startsWith(l.code + '-')
                  ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
            >
              <span>{l.flag}</span>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme()
  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white transition-colors"
      aria-label="Toggle theme"
    >
      {theme === 'light' ? (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )}
    </button>
  )
}

const navLinkClass = (active: boolean) => clsx(
  'text-sm font-medium transition-colors',
  active ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
)

function ClerkNav({ isLanding }: { isLanding: boolean }) {
  const location = useLocation()
  const { t } = useTranslation()
  return (
    <>
      <SignedIn>
        <Link to="/dashboard" className={navLinkClass(location.pathname === '/dashboard')}>
          {t('nav.dashboard')}
        </Link>
        <Link to="/practice" className={navLinkClass(location.pathname === '/practice')}>
          {t('nav.practice')}
        </Link>
        {isDev && (
          <Link to="/memorize" className={navLinkClass(location.pathname === '/memorize')}>
            {t('nav.memorize')}
          </Link>
        )}
        <Link to="/learn" className={navLinkClass(location.pathname === '/learn')}>
          {t('nav.learn')}
        </Link>
        <Link to="/settings" className={navLinkClass(location.pathname === '/settings')}>
          {t('nav.settings')}
        </Link>
        <LanguagePicker />
        <ThemeToggle />
        {/* Hide the "API keys" section from the Clerk user profile modal —
            this isn't a developer product. The CSS rule targets Clerk's
            built-in nav item for that section and collapses it. */}
        <UserButton
          appearance={{
            elements: {
              avatarBox: 'w-8 h-8',
              navbarButton__apiKeys: { display: 'none' },
              profileSectionContent__apiKeys: { display: 'none' },
              profileSection__apiKeys: { display: 'none' },
              page__apiKeys: { display: 'none' },
            },
          }}
        />
      </SignedIn>
      <SignedOut>
        <Link to="/learn" className="text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors">
          {t('nav.learn')}
        </Link>
        <Link to="/sign-in" className="text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors">
          {t('nav.signIn')}
        </Link>
        <LanguagePicker />
        <ThemeToggle />
        <Link to="/sign-up" className="btn-primary text-sm py-2 px-4">
          {t('nav.getStartedFree')}
        </Link>
      </SignedOut>
    </>
  )
}

function NoAuthNav() {
  const location = useLocation()
  const { t } = useTranslation()
  return (
    <>
      <Link to="/dashboard" className={navLinkClass(location.pathname === '/dashboard')}>
        {t('nav.dashboard')}
      </Link>
      <Link to="/practice" className={navLinkClass(location.pathname === '/practice')}>
        {t('nav.practice')}
      </Link>
      {isDev && (
        <Link to="/memorize" className={navLinkClass(location.pathname === '/memorize')}>
          {t('nav.memorize')}
        </Link>
      )}
      <Link to="/learn" className={navLinkClass(location.pathname === '/learn')}>
        {t('nav.learn')}
      </Link>
      <LanguagePicker />
      <ThemeToggle />
      <Link to="/practice" className="btn-primary text-sm py-2 px-4">
        {t('nav.getStartedFree')}
      </Link>
    </>
  )
}

export default function Layout() {
  const location = useLocation()
  const { t } = useTranslation()
  const isLanding = location.pathname === '/'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">
      {isClerkEnabled && <ClerkAuthSync />}

      <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold text-sm">
              ت
            </div>
            <span className="text-lg font-bold text-gray-900 dark:text-white">Tajweed Tutor</span>
          </Link>

          <nav className="flex items-center gap-6">
            {isClerkEnabled ? <ClerkNav isLanding={isLanding} /> : <NoAuthNav />}
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="text-center py-8 text-gray-400 dark:text-gray-600 text-xs border-t border-gray-200 dark:border-gray-900 space-y-1.5">
        <p>{t('footer.tagline')}</p>
        <p className="text-gray-300 dark:text-gray-700 flex items-center justify-center gap-3">
          <Link to="/learn" className="hover:text-emerald-500 transition-colors">{t('footer.learnLink')}</Link>
          <span>·</span>
          <Link to="/contact" className="hover:text-emerald-500 transition-colors">Contact</Link>
        </p>
      </footer>
    </div>
  )
}
